import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Descriptions, Button, Tag, Space, Spin, message, Modal, Table } from 'antd';
import { ArrowLeftOutlined, EditOutlined, DeleteOutlined, SyncOutlined, ShoppingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import hqDisplayGroupService from '../../services/hqDisplayGroupService';
// 修改引用路径，添加国际化状态标签函数
import { DISPLAY_GROUP_STATUS_COLORS, DISPLAY_GROUP_STATUS_LABELS, getStatusLabel } from '../../utils/hqDisplayGroupConstants';
import '../../styles/hqDisplayGroup.css';
import dayjs from 'dayjs';
import { HQDisplayGroup, DisplayGroupStatus } from '../../types/hqDisplayGroup';

const HQDisplayGroupDetail: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(true);
  const [detail, setDetail] = useState<HQDisplayGroup | null>(null);

  useEffect(() => {
    if (code) {
      fetchDisplayGroupDetail(code);
    }
  }, [code]);

  const fetchDisplayGroupDetail = async (code: string) => {
    try {
      setLoading(true);
      const response = await hqDisplayGroupService.getHQDisplayGroupByCode(code);
      
      if (response.success && response.data) {
        // 转换数据格式以适应组件
        const displayGroupData = response.data;
        setDetail({
          id: displayGroupData.id,
          code: displayGroupData.code,
          name: displayGroupData.name,
          status: displayGroupData.status,
          mainDivision: displayGroupData.mainDivision,
          subClassSets: displayGroupData.subClassSets, // 需要根据实际数据结构调整
          subClassCount: displayGroupData.subClassCount,
          whiteListItems: displayGroupData.whiteListItems || [],
          blackListItems: displayGroupData.blackListItems || [],
          remark: displayGroupData.remark,
          gmtCreate: displayGroupData.gmtCreate,
          gmtModified: displayGroupData.gmtModified,
        });
      } else {
        message.error(response.errorMessage || t('hqDisplayGroup.fetchDetailFailed'));
      }
    } catch (error) {
      console.error('Failed to fetch display group details:', error);
      message.error(t('hqDisplayGroup.fetchDetailFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    navigate(`/hq-display-groups/${code}/edit`);
  };

  const handleBack = () => {
    navigate('/hq-display-groups');
  };

  if (loading) {
    return (
      <div className="hq-display-group-detail-loading">
        <Spin size="large" />
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="hq-display-group-detail-error">
        <h2>{t('hqDisplayGroup.notFound')}</h2>
        <Button type="primary" onClick={handleBack}>
          {t('hqDisplayGroup.backToList')}
        </Button>
      </div>
    );
  }

   const formatDateTime = (timestamp: number): string => {
    return dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss');
  };

  return (
    <div className="hq-display-group-detail">
      <div className="detail-header">
        <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
          {t('hqDisplayGroup.backToList')}
        </Button>
        <Space>
          <Button 
            type="primary" 
            icon={<EditOutlined />} 
            onClick={handleEdit}
          >
            {t('hqDisplayGroup.editItem')}
          </Button>
        </Space>
      </div>

      <Card title={t('hqDisplayGroup.detail.title', { name: detail.name })} className="detail-card">
        <Descriptions bordered column={2}>
          <Descriptions.Item label={t('hqDisplayGroup.detail.code')}>{detail.code}</Descriptions.Item>
          <Descriptions.Item label={t('hqDisplayGroup.detail.name')}>{detail.name}</Descriptions.Item>
          <Descriptions.Item label={t('hqDisplayGroup.detail.status')} span={2}>
            <Tag color={DISPLAY_GROUP_STATUS_COLORS[detail.status]}>
              {getStatusLabel(detail.status, t)}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label={t('hqDisplayGroup.detail.mainDivision')} span={2}>
            {detail.mainDivisionName || t('hqDisplayGroup.detail.noRemark')}
          </Descriptions.Item>
          <Descriptions.Item label={t('hqDisplayGroup.detail.categories')} span={2}>
            <div className="tag-list">
              {detail.subClassSets.map(category => (
                <Tag key={category.subClassCode}>{category.subClassName}</Tag>
              ))}
            </div>
          </Descriptions.Item>
          <Descriptions.Item label={t('hqDisplayGroup.detail.whiteList')} span={2}>
            {detail.whiteListItems.length > 0 ? (
              <Table
                dataSource={detail.whiteListItems}
                columns={[
                  {
                    title: t('hqDisplayGroup.detail.itemCodeColumn'),
                    dataIndex: 'code',
                    key: 'code',
                    width: 120,
                  },
                  {
                    title: t('hqDisplayGroup.detail.itemNameColumn'),
                    dataIndex: 'name',
                    key: 'name',
                  },
                ]}
                size="small"
                pagination={false}
                rowKey="id"
                style={{ marginTop: 8 }}
                scroll={{ x: 1200, y: 500 }}
              />
            ) : (
              t('hqDisplayGroup.detail.noItems')
            )}
          </Descriptions.Item>
          <Descriptions.Item label={t('hqDisplayGroup.detail.blackList')} span={2}>
            {detail.blackListItems.length > 0 ? (
              <Table
                dataSource={detail.blackListItems}
                columns={[
                  {
                    title: t('hqDisplayGroup.detail.itemCodeColumn'),
                    dataIndex: 'code',
                    key: 'code',
                    width: 120,
                  },
                  {
                    title: t('hqDisplayGroup.detail.itemNameColumn'),
                    dataIndex: 'name',
                    key: 'name',
                  },
                ]}
                size="small"
                pagination={false}
                rowKey="id"
                style={{ marginTop: 8 }}
                scroll={{ x: 1200, y: 500 }}
              />
            ) : (
              t('hqDisplayGroup.detail.noItems')
            )}
          </Descriptions.Item>
          <Descriptions.Item label={t('hqDisplayGroup.detail.remark')} span={2}>
            {detail.remark || t('hqDisplayGroup.detail.noRemark')}
          </Descriptions.Item>
          <Descriptions.Item label={t('hqDisplayGroup.detail.createTime')}>{formatDateTime(detail.gmtCreate)}</Descriptions.Item>
          <Descriptions.Item label={t('hqDisplayGroup.detail.updateTime')}>{formatDateTime(detail.gmtModified)}</Descriptions.Item>
        </Descriptions>
      </Card>
    </div>
  );
};

export default HQDisplayGroupDetail;